import React, { useState, useEffect, useCallback } from 'react';
import { api } from '../api';
import AccountSecurityPanel from './AccountSecurityPanel';
import ResourceUsagePanel from './ResourceUsagePanel';
import { useLocale } from '../i18n/LocaleContext';

function AccountPanel({ tenant, credits, onTenantUpdated }) {
  const { t, dateLocale } = useLocale();
  // First name for the personal greeting (2026-10-17); existing accounts can add it here.
  const [firstNameInput, setFirstNameInput] = useState(tenant.firstName || '');
  const [nameStatus, setNameStatus] = useState('');
  const [nameSaving, setNameSaving] = useState(false);
  const saveFirstName = async (event) => {
    event.preventDefault();
    setNameSaving(true); setNameStatus('');
    try {
      const res = await api.updateProfile({ firstName: firstNameInput.trim() });
      if (res?.tenant) onTenantUpdated?.(res.tenant);
      setNameStatus(t('account.nameSaved'));
    } catch (err) {
      setNameStatus(err.response?.data?.error || t('account.nameSaveFailed'));
    } finally { setNameSaving(false); }
  };
  const [byokStatus, setByokStatus] = useState(null);
  const [byokInput, setByokInput] = useState('');
  const [byokError, setByokError] = useState('');
  const [invoices, setInvoices] = useState(null);
  const [activity, setActivity] = useState(null);
  const [checkoutError, setCheckoutError] = useState('');
  const [checkoutLoading, setCheckoutLoading] = useState(false);

  const loadAll = useCallback(() => {
    api.byokStatus().then(setByokStatus).catch(() => setByokStatus({ hasKey: false }));
    api.invoices().then(res => setInvoices(res.invoices)).catch(() => setInvoices([]));
    api.myActivity().then(res => setActivity(res.entries)).catch(() => setActivity([]));
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSetByokKey = async (e) => {
    e.preventDefault();
    setByokError('');
    try {
      await api.setByokKey(byokInput);
      setByokInput('');
      loadAll();
    } catch (err) {
      setByokError(err.response?.data?.error || err.message);
    }
  };

  const handleRemoveByokKey = async () => {
    await api.removeByokKey();
    loadAll();
  };

  const handleUpgrade = async () => {
    setCheckoutLoading(true);
    setCheckoutError('');
    try {
      const result = await api.billingCheckout(window.location.href, window.location.href);
      window.location.href = result.url;
    } catch (err) {
      setCheckoutError(err.response?.data?.error || t('account.billingUnavailable'));
      setCheckoutLoading(false);
    }
  };

  return (
    <div className="account-center">
      <div className="account-section">
        <div className="section-heading">{t('account.title')}</div>
        <div className="account-overview-card">
          <div className="account-overview-item">
            <span className="account-overview-label">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M4 4h16v16H4z" opacity="0" /><path d="M22 6 12 13 2 6" /><path d="M2 6h20v12H2z" /></svg>
              {t('account.email')}
            </span>
            <span className="account-overview-value">{tenant.email}</span>
          </div>
          <form className="account-overview-item account-first-name" onSubmit={saveFirstName}>
            <label className="account-overview-label" htmlFor="account-first-name">{t('account.firstName')}</label>
            <input id="account-first-name" type="text" value={firstNameInput} onChange={e => setFirstNameInput(e.target.value)} maxLength={30} autoComplete="given-name" required />
            <button type="submit" className="btn" disabled={nameSaving || !firstNameInput.trim() || firstNameInput.trim() === (tenant.firstName || '')}>{t('account.saveName')}</button>
            {nameStatus && <small role="status">{nameStatus}</small>}
          </form>
          <div className="account-overview-item">
            <span className="account-overview-label">{t('account.plan')}</span>
            <span className="account-plan-badge">{tenant.plan}</span>
          </div>
          <div className="account-overview-item">
            <span className="account-overview-label">{t('account.credits')}</span>
            <span className="account-overview-credits">{credits ?? '—'}</span>
          </div>
        </div>
      </div>

      <AccountSecurityPanel />

      <div className="account-section">
        <div className="section-heading">{t('account.yourKey')}</div>
        <div className="account-card">
          <p style={{ color: 'var(--text-secondary)', fontSize: '13px', marginBottom: '1rem' }}>
            {t('account.keyDescription')}
          </p>
          {byokStatus?.hasKey ? (
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <span style={{ color: 'var(--text-primary)', fontSize: '13.5px' }}>{t('account.keySet')}</span>
              <button type="button" className="btn" onClick={handleRemoveByokKey}>{t('account.removeKey')}</button>
            </div>
          ) : (
            <form onSubmit={handleSetByokKey}>
              <div className="form-group">
                <label>{t('account.apiKey')}</label>
                <input type="password" value={byokInput} onChange={e => setByokInput(e.target.value)} placeholder="sk-ant-... / sk-... / xai-... / AIza..." />
              </div>
              {byokError && <div className="auth-error">{byokError}</div>}
              <button type="submit" className="submit-btn">{t('account.saveKey')}</button>
            </form>
          )}
        </div>
      </div>

      <div className="account-section">
        <div className="section-heading">{t('account.billing')}</div>
        <div className="account-card">
          <div className="account-billing-row">
            <span className="account-billing-label">
              {t('account.currentPlan', { plan: tenant.plan })}
              {tenant.subscriptionActive && <span className="account-billing-active"> · {t('account.subscriptionActive')}</span>}
            </span>
            <button type="button" className="account-upgrade-btn" onClick={handleUpgrade} disabled={checkoutLoading}>
              {checkoutLoading ? t('account.checkout') : t('account.manageSubscription')}
            </button>
          </div>
          {checkoutError && <div className="auth-error" style={{ marginTop: '0.75rem' }}>{checkoutError}</div>}
        </div>
      </div>

      <ResourceUsagePanel />

      <div className="account-section">
        <div className="section-heading">{t('account.invoices')}</div>
        <div className="account-timeline-card">
          <div className="activity-list">
            {invoices === null && <div className="no-logs">{t('common.loading')}</div>}
            {invoices?.length === 0 && <div className="no-logs">{t('account.noInvoices')}</div>}
            {invoices?.map(inv => (
              <div className="activity-item" key={inv.number}>
                <span className="activity-time">#{inv.number}</span>
                <span className="activity-message">{inv.currency} {inv.amount} — {new Date(inv.issuedAt).toLocaleDateString(dateLocale)}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="account-section">
        <div className="section-heading">{t('account.activity')}</div>
        <div className="account-timeline-card">
          <div className="activity-list">
            {activity === null && <div className="no-logs">{t('common.loading')}</div>}
            {activity?.length === 0 && <div className="no-logs">{t('account.noActivity')}</div>}
            {activity?.map((entry, i) => (
              <div className="activity-item" key={i}>
                <span className="activity-time">{new Date(entry.timestamp).toLocaleDateString(dateLocale)}</span>
                <span className="activity-message">{entry.action.replace(/_/g, ' ')}</span>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccountPanel;
